import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { communications, documents, finance, parties as partiesApi, requests } from '../../api/endpoints';
import type {
    ActCompareResponse,
    Document,
    ExtractedField,
    ExtractionRun,
    ExtractionRunDetail,
    FinCandidatesResponse,
    Invoice,
    Message, MessageRequest,
    OwnLegalEntity, Party,
    Product, RequestParty
} from '../../api/types';
import {
    Card, Empty, ErrorBox, Field, fmtDate, KV, shortId, StatusBadge,
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const RUN_STATUS: Record<string, string> = {
  pending: 'В очереди', running: 'Выполняется', done: 'Завершено',
  partial: 'Частично', error: 'Ошибка',
};

const RUN_ERROR_TEXT: Record<string, string> = {
  ocr_unavailable: 'OCR-движок недоступен на сервере (tesseract не установлен) — сканы и изображения не распознаются. Текстовые документы обрабатываются движком правил.',
  source_quarantined: 'Исходный файл не прошёл проверку безопасности и заблокирован.',
  file_quarantined: 'Исходный файл не прошёл проверку безопасности и заблокирован.',
  extraction_failed: 'Движок извлечения завершился ошибкой.',
  no_fields_extracted: 'Из документа не извлечено ни одного поля — разметка не распознана.',
  ai_not_implemented: 'Внешний ИИ-движок извлечения не реализован.',
  ai_not_configured: 'Внешний ИИ не настроен на сервере (нет DEEPSEEK_API_KEY).',
  ai_request_failed: 'ИИ-сервис недоступен или вернул ошибку.',
  ai_bad_response: 'ИИ вернул ответ не в ожидаемом JSON-формате.',
  ai_text_only: 'Внешний ИИ обрабатывает только текстовые источники и PDF с текстовым слоем — сканы/изображения идут через OCR.',
  ai_empty_source: 'В источнике нет текста для ИИ-извлечения.',
  max_attempts: 'Прогон извлечения исчерпал максимум попыток.',
  page_limit: 'Документ длиннее лимита страниц для OCR — результат неполный, извлечение не выполнено.',
  extraction_timeout: 'Извлечение превысило лимит времени.',
};

// === WT2 extract ===
const PROFILE_LABEL: Record<string, string> = { invoice: 'Счёт', act: 'Акт' };
const LIMIT_REASON_LABEL: Record<string, string> = {
  ai_not_configured: 'ИИ не настроен (нет ключа DEEPSEEK_API_KEY)',
  ai_request_failed: 'запрос к ИИ завершился ошибкой',
  ai_bad_response: 'ИИ вернул некорректный ответ',
  ai_text_only: 'ИИ принимает только текстовые источники',
  ai_empty_source: 'нет текста для отправки в ИИ',
};
// Поля карточки компании (§7) — отдельное подтверждение, цель 'party'.
const PARTY_CARD_RE = /^(applicant|manufacturer)\.(legal_address|tax_id|kpp|registration_id|signatory_name|signatory_basis)$|^manufacturer_country$/;
const FIELD_LABELS: Record<string, string> = {
  'request_text': 'Текст запроса клиента',
  'applicant.name': 'Заявитель — наименование',
  'applicant_country': 'Заявитель — страна',
  'applicant.legal_address': 'Заявитель — юр. адрес',
  'applicant.tax_id': 'Заявитель — ИНН/БИН/ИИН',
  'applicant.kpp': 'Заявитель — КПП',
  'applicant.registration_id': 'Заявитель — ОГРН/рег. номер',
  'applicant.signatory_name': 'Заявитель — подписант',
  'applicant.signatory_basis': 'Заявитель — основание подписанта',
  'manufacturer.name': 'Изготовитель — наименование',
  'manufacturer_country': 'Изготовитель — страна',
  'manufacturer.legal_address': 'Изготовитель — юр. адрес',
  'manufacturer.tax_id': 'Изготовитель — ИНН/БИН/ИИН',
  'manufacturer.kpp': 'Изготовитель — КПП',
  'manufacturer.registration_id': 'Изготовитель — рег. номер',
  'manufacturer.signatory_name': 'Изготовитель — подписант',
  'manufacturer.signatory_basis': 'Изготовитель — основание подписанта',
  'country_of_entry': 'Страна ввоза',
  'invoice.number': 'Номер счёта', 'invoice.date': 'Дата счёта',
  'invoice.currency': 'Валюта', 'invoice.total_amount': 'Итого по счёту',
  'invoice.payer_name': 'Плательщик', 'invoice.payer_tax_id': 'ИНН плательщика',
  'invoice.payee_name': 'Получатель (наша организация)',
  'invoice.payee_tax_id': 'ИНН получателя',
  'act.number': 'Номер акта', 'act.date': 'Дата акта',
  'act.currency': 'Валюта', 'act.total_amount': 'Итого по акту',
  'act.payer_name': 'Заказчик', 'act.payer_tax_id': 'ИНН заказчика',
  'act.payee_name': 'Исполнитель', 'act.payee_tax_id': 'ИНН исполнителя',
  'act.invoice_number': 'Номер счёта (основание)',
};
const COMPARE_ROW_LABEL: Record<string, string> = {
  invoice_number: 'Номер счёта', payer_name: 'Плательщик',
  payer_tax_id: 'ИНН плательщика', payee_name: 'Получатель',
  payee_tax_id: 'ИНН получателя', currency: 'Валюта', total_amount: 'Итого',
};
const COMPARE_RESULT_LABEL: Record<string, string> = {
  match: 'совпадает', mismatch: 'расходится', missing: 'нет в акте',
  partial: 'частичный акт',
};

function fieldLabel(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  const m = path.match(/^(invoice_lines|act_lines|products)\[(\d+)\]\.(\w+)$/);
  if (m) {
    const attr: Record<string, string> = {
      name: 'наименование', model: 'модель', article: 'артикул',
      tnved: 'ТН ВЭД', description: 'работа/услуга', amount: 'сумма',
      quantity: 'кол-во', unit_price: 'цена',
    };
    return `Строка ${Number(m[2]) + 1} — ${attr[m[3]] || m[3]}`;
  }
  return path;
}

function cmpBadge(result?: string) {
  const cls = result === 'match' ? 'ok'
    : result === 'partial' ? 'info'
    : result === 'missing' ? 'warn' : 'bad';
  return <span className={`badge ${cls}`}>{COMPARE_RESULT_LABEL[result || ''] || result}</span>;
}

const ITEM_FIELD_ROOTS = new Set(['tnved', 'country_of_entry', 'document_type', 'title', 'item']);
const PARTY_FIELD_ROOTS = new Set(['applicant', 'payer', 'manufacturer', 'consignee']);

interface DecisionState {
  action: '' | 'accept' | 'keep' | 'correct';
  value: string;
  targetKind: string;
  targetId: string;
}

function proposedValue(f: ExtractedField): string {
  return f.normalized_value || f.raw_value || '';
}

function fieldCurrent(detail: ExtractionRunDetail | null, f: ExtractedField): string {
  if (f.current_value != null && f.current_value !== '') return f.current_value;
  return detail?.current_values?.[f.field_path]?.value ?? '';
}

/** Цель применения по умолчанию из field_path (запасной вариант, если
 *  detail-endpoint не вернул target_*): products[i].* → продукт позиции
 *  (нет продукта с таким индексом → пустой id = создать новый, а не чужой
 *  products[0]); роли участников и item-поля → позиция заявки. */
function defaultTarget(fieldPath: string, ctx: {
  products: Product[]; parties: RequestParty[]; itemId: string; requestId: string;
}): { kind: string; id: string } {
  const pm = fieldPath.match(/^products\[(\d+)\]\./);
  if (pm) {
    const idx = Number(pm[1]);
    return { kind: 'product', id: ctx.products[idx]?.id ?? '' };
  }
  // === WT2 extract ===
  // Поля карточки компании — только цель 'party' (отдельное подтверждение §7).
  if (PARTY_CARD_RE.test(fieldPath)) return { kind: 'party', id: '' };
  const root = fieldPath.split('.')[0].split('[')[0];
  if (PARTY_FIELD_ROOTS.has(root) || ITEM_FIELD_ROOTS.has(root)) {
    return { kind: 'item', id: ctx.itemId || '' };
  }
  return { kind: 'request', id: ctx.requestId };
}

/** Серверная цель из detail-ответа: backend уже вычислил kind/id/revision —
 *  UI отправляет их обратно, а не вычисляет свои. */
function serverTarget(detail: ExtractionRunDetail | null, f: ExtractedField):
    { kind: string; id: string } | null {
  const kind = f.target_kind ?? detail?.current_values?.[f.field_path]?.target_kind;
  const id = f.target_id ?? detail?.current_values?.[f.field_path]?.target_id;
  if (!kind) return null;
  return { kind, id: id ?? '' };
}

// === WT2 extract ===
/** Кликабельный источник поля: страница → открытие файла, фрагмент — в title. */
function EvidenceCell({ f, blobId }: {
  f: ExtractedField; blobId?: string;
}) {
  const open = () => {
    if (!blobId) return;
    void (async () => {
      let url = documents.downloadInlineUrl(blobId);
      try {
        const preview = await documents.filePreview(blobId);
        if (preview.url) url = preview.url;
      } catch { /* оригинал — тоже ок */ }
      window.open(`${url}${f.page ? `#page=${f.page}` : ''}`, '_blank');
    })();
  };
  return (
    <td className="muted" title={f.evidence ?? ''}>
      {blobId
        ? <a href="#" onClick={(e) => { e.preventDefault(); open(); }}>
            {f.page != null ? `стр. ${f.page}` : 'файл'}
          </a>
        : (f.page != null ? `стр. ${f.page}` : '')}
      {f.evidence ? ` ${f.evidence.slice(0, 60)}${f.evidence.length > 60 ? '…' : ''}` : ''}
      {f.page == null && !f.evidence ? '—' : ''}
    </td>
  );
}

// === WT2 extract ===
/** §7: альтернативные значения того же поля из других мест документа —
 *  честный выбор вместо молчаливого первого. */
function Alternatives({ f, onPick }: {
  f: ExtractedField; onPick: (value: string) => void;
}) {
  const alts = f.alternatives || [];
  if (!alts.length) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <div className="muted" style={{ fontSize: 12 }}>Другие варианты в документе:</div>
      {alts.map((a, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
          <span style={{ fontSize: 12 }}>
            {a.normalized_value || a.raw_value || '—'}
            {a.page != null ? ` (стр. ${a.page})` : ''}
          </span>
          <button type="button" className="btn small"
                  onClick={() => onPick(a.normalized_value || a.raw_value || '')}>
            Применить
          </button>
        </div>
      ))}
    </div>
  );
}

// === WT2 extract ===
/** §8: кандидаты заявок, распределение суммы, регистрация счёта /
 *  сравнение и регистрация акта. Автопривязки нет — выбор ручной. */
function FinanceSection({ runId, docKind, onChanged }: {
  runId: string; docKind: string; onChanged: () => void;
}) {
  const fin = useAsync<FinCandidatesResponse>(
    () => documents.extractionCandidates(runId), [runId]);
  const issuers = useAsync<OwnLegalEntity[]>(
    () => finance.ownLegalEntities().catch(() => [] as OwnLegalEntity[]), []);
  const allParties = useAsync<Party[]>(
    () => partiesApi.list({ page_size: 500 }).catch(() => [] as Party[]), []);
  const payerChoices = useAsync<Array<{ id: string; name: string }>>(
    () => (fin.data && !fin.data.payer
      ? finance.payerChoices().catch(() => [])
      : Promise.resolve([])),
    [fin.data]);

  const [issuerId, setIssuerId] = useState('');
  const [payerId, setPayerId] = useState('');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState('');
  const [currency, setCurrency] = useState('');
  const [total, setTotal] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [invoiceId, setInvoiceId] = useState('');
  const [cmp, setCmp] = useState<ActCompareResponse | null>(null);
  const [actAlloc, setActAlloc] = useState<Record<string, string>>({});
  const [err, setErr] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const v = fin.data?.values;
    if (!v) return;
    setNumber(v.number || '');
    setDate(v.date || '');
    setCurrency(v.currency || '');
    setTotal(v.total_amount || '');
    setPayerId(fin.data?.payer?.id || '');
  }, [fin.data]);

  useEffect(() => {
    if (!issuerId && issuers.data?.length) setIssuerId(issuers.data[0].id);
  }, [issuers.data, issuerId]);

  const partyName = useMemo(() => {
    const map = new Map((allParties.data ?? []).map((p) => [p.id, p.name]));
    return (id?: string | null) => (id ? map.get(id) || id : '—');
  }, [allParties.data]);

  const invoices = useAsync<Invoice[]>(
    () => (docKind === 'act' && payerId
      ? finance.invoices({ payer: payerId, page_size: 100 }).catch(() => [] as Invoice[])
      : Promise.resolve([] as Invoice[])),
    [docKind, payerId]);

  const allocTotal = useMemo(() => Object.entries(alloc)
    .filter(([k]) => checked[k])
    .reduce((s, [, v]) => s + (Number(v.replace(',', '.')) || 0), 0),
    [alloc, checked]);
  const totalNum = Number((total || '').replace(',', '.')) || 0;
  const overAllocated = totalNum > 0 && allocTotal > totalNum + 0.005;

  const registerInvoice = () => {
    const lines = Object.entries(checked)
      .filter(([k, v]) => v && alloc[k])
      .map(([item_id]) => ({ item_id, amount: alloc[item_id].replace(',', '.') }));
    if (!issuerId || !payerId) { setErr(new Error('Выберите организацию и плательщика.')); return; }
    if (!lines.length) { setErr(new Error('Отметьте хотя бы одну позицию распределения.')); return; }
    if (overAllocated) { setErr(new Error('Сумма распределения превышает итог счёта.')); return; }
    setBusy(true); setErr(null); setNotice('');
    documents.extractionRegisterInvoice(runId, {
      issuer_id: issuerId, payer_id: payerId,
      number, date, currency, total, lines,
    }).then((r) => {
      setNotice(`Счёт ${r.number || ''} зарегистрирован (id ${shortId(r.invoice_id)}).`);
      onChanged();
    }).catch(setErr).finally(() => setBusy(false));
  };

  const compareAct = () => {
    if (!invoiceId) { setErr(new Error('Выберите счёт для сравнения.')); return; }
    setBusy(true); setErr(null);
    documents.extractionActCompare(runId, invoiceId)
      .then((r) => {
        setCmp(r);
        const next: Record<string, string> = {};
        for (const row of r.line_rows) {
          if (row.invoice_line_id && row.act_amount) next[row.invoice_line_id] = row.act_amount;
        }
        setActAlloc(next);
      })
      .catch(setErr)
      .finally(() => setBusy(false));
  };

  const registerAct = () => {
    const lines = Object.entries(actAlloc)
      .filter(([, v]) => v && Number(v.replace(',', '.')) > 0)
      .map(([invoice_line_id, amount]) => ({ invoice_line_id, amount: amount.replace(',', '.') }));
    if (!issuerId || !payerId) { setErr(new Error('Выберите организацию и плательщика.')); return; }
    if (!lines.length) { setErr(new Error('Заполните суммы строк акта.')); return; }
    setBusy(true); setErr(null); setNotice('');
    documents.extractionRegisterAct(runId, {
      issuer_id: issuerId, payer_id: payerId, number, date, currency, lines,
    }).then((r) => {
      setNotice(`Акт ${r.number || ''} зарегистрирован (id ${shortId(r.id)}).`);
      onChanged();
    }).catch(setErr).finally(() => setBusy(false));
  };

  if (fin.loading) return <div className="loading">Подбор кандидатов…</div>;
  if (fin.error || !fin.data) {
    return <ErrorBox error={fin.error ?? new Error('Не удалось получить кандидатов.')} onRetry={fin.reload} />;
  }
  const data = fin.data;

  return (
    <Card title={docKind === 'act'
      ? 'Акт — кандидаты, сравнение со счётом и регистрация'
      : 'Счёт — кандидаты заявок и распределение'}>
      <ErrorBox error={err} />
      {notice && <div className="alert alert-info">{notice}</div>}

      {data.duplicates?.length > 0 && (
        <div className="alert alert-warn">
          <strong>Возможные дубли:</strong>
          <ul style={{ margin: '6px 0 0 16px' }}>
            {data.duplicates.map((d) => (
              <li key={d.invoice_id}>
                счёт №{d.number} от {d.date} ({d.payer_name}) — {d.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-row">
        <Field label="Номер">
          <input value={number} onChange={(e) => setNumber(e.target.value)} />
        </Field>
        <Field label="Дата">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Валюта">
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: 90 }} />
        </Field>
        <Field label="Итого">
          <input value={total} onChange={(e) => setTotal(e.target.value)} style={{ width: 130 }} />
        </Field>
        <Field label="Организация (issuer)">
          <select value={issuerId} onChange={(e) => setIssuerId(e.target.value)}>
            <option value="">— выберите —</option>
            {(issuers.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>{partyName(o.party)}</option>
            ))}
          </select>
        </Field>
        <Field label="Плательщик">
          {data.payer ? (
            <span>{data.payer.name}{data.payer.tax_id ? ` (ИНН ${data.payer.tax_id})` : ''}</span>
          ) : (
            <select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
              <option value="">— не распознан, выберите —</option>
              {(payerChoices.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
        </Field>
      </div>
      {data.values?.payer_name && !data.payer && (
        <div className="field-hint">
          В документе указан плательщик «{data.values.payer_name}»
          {data.values.payer_tax_id ? ` (ИНН ${data.values.payer_tax_id})` : ''} —
          совпадения в справочнике не найдено.
        </div>
      )}

      <h3 style={{ margin: '12px 0 6px' }}>Заявки-кандидаты</h3>
      {data.candidates.length === 0 ? (
        <Empty text="Подходящих заявок не найдено — автоматическая привязка не выполняется" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Заявка</th><th>Основания совпадения</th>
                {docKind === 'invoice' && <th>Позиции и распределение суммы</th>}
              </tr>
            </thead>
            <tbody>
              {data.candidates.map((c) => (
                <tr key={c.request_id}>
                  <td>
                    <div>{c.number}</div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {c.party_name || ''}{c.stage ? ` · ${c.stage}` : ''}
                    </div>
                  </td>
                  <td>
                    {c.reasons.length
                      ? c.reasons.map((r, i) => (
                          <div key={i} className="muted" style={{ fontSize: 12 }}>• {r}</div>
                        ))
                      : <span className="muted">—</span>}
                  </td>
                  {docKind === 'invoice' && (
                    <td>
                      {c.items.map((it) => (
                        <div key={it.item_id}
                             style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
                          <label style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                            <input type="checkbox"
                              checked={!!checked[it.item_id]}
                              onChange={(e) => setChecked({ ...checked, [it.item_id]: e.target.checked })} />
                            <span>
                              {it.title || shortId(it.item_id)}
                              {it.agreed_sale ? ` — ${it.agreed_sale} ${it.currency || ''}` : ''}
                              {it.matched && (
                                <span className="badge ok" style={{ marginLeft: 6 }}>сумма совпала</span>
                              )}
                            </span>
                          </label>
                          <input style={{ width: 110 }} placeholder="сумма"
                            value={alloc[it.item_id] ?? (it.matched ? (it.agreed_sale || '') : '')}
                            onChange={(e) => {
                              setAlloc({ ...alloc, [it.item_id]: e.target.value });
                              if (e.target.value) setChecked({ ...checked, [it.item_id]: true });
                            }} />
                        </div>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.candidates.length > 1 && (
        <div className="field-hint">
          Найдено несколько кандидатов — автоматическая привязка не выполняется,
          выберите позиции и суммы вручную.
        </div>
      )}

      {docKind === 'invoice' && (
        <>
          <div className="kv" style={{ marginTop: 8 }}>
            <span className="kv-key">Распределено</span>
            <span className="kv-value">
              {allocTotal.toFixed(2)} {currency}
              {totalNum > 0 && ` / ${totalNum.toFixed(2)}`}
              {overAllocated && (
                <span className="badge bad" style={{ marginLeft: 6 }}>превышение итога</span>
              )}
            </span>
          </div>
          <div className="form-actions">
            <button className="btn primary" disabled={busy || overAllocated}
                    onClick={registerInvoice}>
              {busy ? 'Регистрация…' : 'Зарегистрировать счёт'}
            </button>
          </div>
        </>
      )}

      {docKind === 'act' && (
        <>
          <div className="form-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
            <Field label="Счёт для сравнения" hint="Счета плательщика из документа">
              <select value={invoiceId}
                      onChange={(e) => { setInvoiceId(e.target.value); setCmp(null); }}>
                <option value="">— выберите счёт —</option>
                {(invoices.data ?? []).map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    №{inv.number} от {inv.date} — {inv.total} {inv.currency}
                  </option>
                ))}
              </select>
            </Field>
            <button className="btn" disabled={busy || !invoiceId} onClick={compareAct}>
              Сравнить со счётом
            </button>
          </div>

          {cmp && (
            <>
              <div style={{ margin: '8px 0' }}>
                {cmp.partial && <span className="badge info">частичный акт</span>}
                {cmp.has_mismatch && (
                  <span className="badge warn" style={{ marginLeft: 6 }}>есть расхождения</span>
                )}
                {!cmp.has_mismatch && !cmp.partial && (
                  <span className="badge ok">расхождений нет</span>
                )}
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Поле</th><th>Счёт</th><th>Акт</th><th>Результат</th></tr>
                  </thead>
                  <tbody>
                    {cmp.rows.map((r) => (
                      <tr key={r.field} className={r.result === 'mismatch' ? 'mismatch' : ''}>
                        <td>{COMPARE_ROW_LABEL[r.field] || r.field}</td>
                        <td>{r.invoice_value ?? '—'}</td>
                        <td>{r.act_value ?? '—'}</td>
                        <td>{cmpBadge(r.result)}</td>
                      </tr>
                    ))}
                    {cmp.line_rows.map((r, i) => (
                      <tr key={`line-${i}`} className={r.result === 'mismatch' ? 'mismatch' : ''}>
                        <td>
                          Строка {i + 1}
                          {r.invoice_description ? ` — ${r.invoice_description}` : ''}
                        </td>
                        <td>{r.invoice_amount ?? '—'}</td>
                        <td>{r.act_amount ?? r.act_description ?? '—'}</td>
                        <td>{cmpBadge(r.result)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 style={{ margin: '12px 0 6px' }}>Регистрация акта (черновик)</h3>
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Строка счёта</th><th>Сумма по акту</th></tr></thead>
                  <tbody>
                    {cmp.line_rows.filter((r) => r.invoice_line_id).map((r, i) => (
                      <tr key={r.invoice_line_id}>
                        <td>{r.invoice_description || `строка ${i + 1}`} ({r.invoice_amount ?? '—'})</td>
                        <td>
                          <input style={{ width: 130 }}
                            value={actAlloc[r.invoice_line_id!] ?? ''}
                            onChange={(e) => setActAlloc({
                              ...actAlloc, [r.invoice_line_id!]: e.target.value,
                            })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button className="btn primary" disabled={busy} onClick={registerAct}>
                  {busy ? 'Регистрация…' : 'Зарегистрировать акт'}
                </button>
              </div>
              <div className="field-hint">
                Подтверждённые финансовые записи автоматически не изменяются —
                расхождения обрабатываются вручную.
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

export function ExtractionTab({ data }: { data: CardData }) {
  const requestId = data.request.id;

  // Источники извлечения: сообщения заявки и версии документов.
  const links = useAsync<MessageRequest[]>(
    () => communications.messageLinks({ request: requestId }).catch(() => [] as MessageRequest[]),
    [requestId],
  );
  const msgs = useAsync<Message[]>(
    () => communications.messages({ request: requestId }).catch(() => [] as Message[]),
    [requestId],
  );
  const docs = useAsync<Document[]>(
    () => documents.documents({ request: requestId }).catch(() => [] as Document[]),
    [requestId],
  );
  const products = useAsync<Product[]>(
    () => requests.products(requestId).catch(() => [] as Product[]),
    [requestId],
  );
  const reqParties = useAsync<RequestParty[]>(
    () => requests.parties(requestId).catch(() => [] as RequestParty[]),
    [requestId],
  );

  const [sourceKind, setSourceKind] = useState<'message' | 'version'>('message');
  const [sourceId, setSourceId] = useState('');
  const [engine, setEngine] = useState('');
  const [docKind, setDocKind] = useState(''); // === WT2 extract === (''|'invoice'|'act')
  const [itemId, setItemId] = useState(data.items[0]?.id ?? '');
  const [runId, setRunId] = useState('');
  const [startErr, setStartErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const requestMessages = useMemo(() => {
    const all = msgs.data ?? [];
    const linked = new Set((links.data ?? []).map((l) => l.message));
    return linked.size > 0 ? all.filter((m) => linked.has(m.id)) : all;
  }, [msgs.data, links.data]);

  const versions = useMemo(
    () => (docs.data ?? []).filter((d) => d.current_version),
    [docs.data],
  );

  // История запусков по выбранному источнику.
  const runsHistory = useAsync<ExtractionRun[]>(
    () => {
      if (!sourceId) return Promise.resolve([]);
      return documents.extractionRuns(
        sourceKind === 'message' ? { message: sourceId } : { version: sourceId },
      ).catch(() => []);
    },
    [sourceId, sourceKind],
  );

  const detail = useAsync<ExtractionRunDetail | null>(
    () => (runId ? documents.extractionDetail(runId) : Promise.resolve(null)),
    [runId],
  );

  const run = useMemo(() => {
    const d = detail.data;
    if (!d) return null;
    if (d.run) return d.run;
    if (d.id) return d as unknown as ExtractionRun;
    return null;
  }, [detail.data]);

  const runStatus = run?.status ?? detail.data?.status ?? '';
  const runDocKind = run?.doc_kind ?? detail.data?.doc_kind ?? '';
  const runLimited = run?.limited ?? detail.data?.limited ?? false;
  const runLimitReason = run?.limit_reason ?? detail.data?.limit_reason ?? '';

  // Поллинг пока run в работе.
  useEffect(() => {
    if (!runId || (runStatus !== 'pending' && runStatus !== 'running')) return;
    const t = window.setInterval(() => detail.reload(), 2500);
    return () => window.clearInterval(t);
  }, [runId, runStatus, detail.reload]);

  const fields = useMemo<ExtractedField[]>(() => {
    const d = detail.data;
    return d?.fields ?? d?.extracted_fields ?? [];
  }, [detail.data]);

  // === WT2 extract ===
  const source = detail.data?.source;
  const partyFields = useMemo(
    () => fields.filter(
      (f) => f.target_kind === 'party' || PARTY_CARD_RE.test(f.field_path)),
    [fields]);
  const requestFields = useMemo(
    () => fields.filter((f) => !partyFields.includes(f)),
    [fields, partyFields]);

  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({});
  const [confirmErr, setConfirmErr] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmNotice, setConfirmNotice] = useState('');

  const targetCtx = useMemo(() => ({
    products: products.data ?? [],
    parties: reqParties.data ?? [],
    itemId,
    requestId,
  }), [products.data, reqParties.data, itemId, requestId]);

  const decisionOf = (f: ExtractedField): DecisionState => {
    const existing = decisions[f.field_path];
    if (existing) return existing;
    const t = serverTarget(detail.data, f) ?? defaultTarget(f.field_path, targetCtx);
    return { action: '', value: '', targetKind: t.kind, targetId: t.id };
  };

  const setDecision = (f: ExtractedField, patch: Partial<DecisionState>) => {
    setDecisions((prev) => ({ ...prev, [f.field_path]: { ...decisionOf(f), ...patch } }));
  };

  const decidedCount = Object.values(decisions).filter((d) => d.action).length;

  const acceptAllDiffs = () => {
    setDecisions((prev) => {
      const next = { ...prev };
      for (const f of fields) {
        const cur = fieldCurrent(detail.data, f);
        if (proposedValue(f) && proposedValue(f) !== cur) {
          const base = next[f.field_path] ?? (() => {
            const t = serverTarget(detail.data, f) ?? defaultTarget(f.field_path, targetCtx);
            return { action: '', value: '', targetKind: t.kind, targetId: t.id };
          })();
          next[f.field_path] = { ...base, action: 'accept' };
        }
      }
      return next;
    });
  };

  const start = async () => {
    if (!sourceId) return;
    setBusy(true); setStartErr(null); setConfirmNotice('');
    try {
      const res = await documents.extractionStart({
        ...(sourceKind === 'message' ? { message_id: sourceId } : { version_id: sourceId }),
        ...(itemId ? { item_id: itemId } : {}),
        ...(engine ? { engine } : {}),
        ...(docKind ? { doc_kind: docKind } : {}),
      });
      const id = res?.id ?? res?.run_id;
      if (id) setRunId(id);
      setDecisions({});
      runsHistory.reload();
    } catch (x) { setStartErr(x); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setConfirmErr(null); setConflict(false); setConfirmNotice('');
    try {
      // Карта ревизий целей: заявка + позиции + продукты + серверные target_*
      // из detail (проверка 409 на бэкенде).
      const targetRevisions: Record<string, number> = {};
      if (data.request.revision != null) targetRevisions[requestId] = data.request.revision;
      for (const it of data.items) {
        if (it.revision != null) targetRevisions[it.id] = it.revision;
      }
      for (const p of products.data ?? []) {
        if (p.revision != null) targetRevisions[p.id] = p.revision;
      }
      for (const f of fields) {
        if (f.target_id && f.target_revision != null) targetRevisions[f.target_id] = f.target_revision;
      }
      const payload = Object.entries(decisions)
        .filter(([, d]) => d.action)
        .map(([field_path, d]) => {
          const f = fields.find((x) => x.field_path === field_path);
          const revision = f?.target_revision ?? (d.targetId ? targetRevisions[d.targetId] : undefined);
          return {
            field_path,
            action: d.action as 'accept' | 'keep' | 'correct',
            ...(d.action === 'correct' && d.value ? { value: d.value } : {}),
            ...(d.targetKind ? { target_kind: d.targetKind } : {}),
            ...(d.targetId ? { target_id: d.targetId } : {}),
            ...(revision != null ? { target_revision: revision } : {}),
          };
        });
      await documents.extractionConfirm(runId, {
        decisions: payload,
        target_revision: data.request.revision,
        target_revisions: targetRevisions,
      });
      setConfirmNotice(`Подтверждено полей: ${payload.length}.`);
      setDecisions({});
      detail.reload();
      runsHistory.reload();
      data.reload();
    } catch (x) {
      if (x instanceof ApiError && x.isConflict) {
        setConflict(true);
      } else {
        setConfirmErr(x);
      }
    } finally { setBusy(false); }
  };

  // === WT2 extract ===
  /** Таблица кандидатов одной группы: данные заявки или карточка компании. */
  const renderFieldsTable = (fs: ExtractedField[], isPartyCard: boolean) => (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Поле</th><th>Текущее значение</th><th>Предложенное</th>
            <th>Источник</th><th>Решение</th><th>Цель</th>
          </tr>
        </thead>
        <tbody>
          {fs.map((f) => {
            const cur = fieldCurrent(detail.data, f);
            const proposed = proposedValue(f);
            const diff = Boolean(proposed) && proposed !== cur;
            const dec = decisionOf(f);
            const isProduct = /^products\[\d+\]\./.test(f.field_path);
            return (
              <tr key={f.field_path} className={diff ? 'mismatch' : ''}>
                <td>
                  <div>{fieldLabel(f.field_path)}</div>
                  <code className="muted" style={{ fontSize: 11 }}>{f.field_path}</code>
                </td>
                <td>{cur || <span className="muted">—</span>}</td>
                <td>
                  {proposed || <span className="muted">—</span>}
                  <Alternatives f={f}
                    onPick={(v) => setDecision(f, { action: 'correct', value: v })} />
                </td>
                <EvidenceCell f={f} blobId={source?.file_blob_id} />
                <td>
                  <select aria-label="Решение" value={dec.action}
                          onChange={(e) => setDecision(f, { action: e.target.value as DecisionState['action'] })}>
                    <option value="">—</option>
                    <option value="accept">Принять</option>
                    <option value="keep">Оставить текущее</option>
                    <option value="correct">Исправить</option>
                  </select>
                  {dec.action === 'correct' && (
                    <input aria-label="Исправленное значение" style={{ marginTop: 4, display: 'block' }}
                           value={dec.value} placeholder="Исправленное значение"
                           onChange={(e) => setDecision(f, { value: e.target.value })} />
                  )}
                </td>
                <td>
                  {isProduct ? (
                    <select aria-label="Целевой продукт" value={dec.targetId}
                            onChange={(e) => setDecision(f, { targetKind: 'product', targetId: e.target.value })}>
                      <option value="">+ Новый продукт (позиция)</option>
                      {(products.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>{p.name || shortId(p.id)}</option>
                      ))}
                    </select>
                  ) : isPartyCard ? (
                    <span className="muted">
                      карточка компании{dec.targetId ? ` · ${shortId(dec.targetId)}` : ' — контрагент не привязан'}
                    </span>
                  ) : (
                    <span className="muted">{dec.targetKind}{dec.targetId ? ` · ${shortId(dec.targetId)}` : ''}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <Card title="Запуск извлечения">
        <ErrorBox error={startErr} />
        <div className="form-row">
          <Field label="Источник">
            <select value={sourceKind}
                    onChange={(e) => { setSourceKind(e.target.value as 'message' | 'version'); setSourceId(''); }}>
              <option value="message">Сообщение канала</option>
              <option value="version">Версия документа</option>
            </select>
          </Field>
          <Field label={sourceKind === 'message' ? 'Сообщение' : 'Документ'}>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              <option value="">— выберите —</option>
              {sourceKind === 'message'
                ? requestMessages.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.sender || '—'} · {fmtDate(m.sent_at ?? m.created_at)} · {(m.subject || m.body || '').slice(0, 60)}
                    </option>
                  ))
                : versions.map((d) => (
                    <option key={d.current_version} value={d.current_version!}>
                      {d.type || 'документ'} · v{shortId(d.current_version)}
                    </option>
                  ))}
            </select>
          </Field>
          <Field label="Профиль документа" hint="Счёт/акт — финансовое извлечение §8">
            <select value={docKind} onChange={(e) => setDocKind(e.target.value)}>
              <option value="">Заявка / общий</option>
              <option value="invoice">Счёт</option>
              <option value="act">Акт</option>
            </select>
          </Field>
          <Field label="Позиция заявки">
            <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
              <option value="">— не указана —</option>
              {data.items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
            </select>
          </Field>
          <Field label="Движок" hint="ИИ требует настроенный DEEPSEEK_API_KEY">
            <select value={engine} onChange={(e) => setEngine(e.target.value)}>
              <option value="">По настройке сервера</option>
              <option value="text_rules">Правила (локально)</option>
              <option value="external_ai">ИИ (DeepSeek)</option>
            </select>
          </Field>
          <button className="btn primary" disabled={busy || !sourceId} onClick={() => void start()}>
            {busy ? 'Запуск…' : 'Запустить извлечение'}
          </button>
        </div>
        {(runsHistory.data ?? []).length > 0 && (
          <div className="field-hint" style={{ marginTop: 4 }}>
            Запуски по источнику:{' '}
            {(runsHistory.data ?? []).map((r) => (
              <a key={r.id} href="#"
                 onClick={(e) => { e.preventDefault(); setRunId(r.id); setDecisions({}); }}
                 style={{ marginRight: 8 }}>
                {shortId(r.id)}
                {r.doc_kind ? ` · ${PROFILE_LABEL[r.doc_kind] || r.doc_kind}` : ''}
                {` · ${RUN_STATUS[r.status ?? ''] ?? r.status}`}
                {r.limited ? ' · ограниченный' : ''}
              </a>
            ))}
          </div>
        )}
      </Card>

      {runId && (
        <Card title={`Результат извлечения ${shortId(runId)}${runDocKind ? ` — ${PROFILE_LABEL[runDocKind] || runDocKind}` : ''}`}
              actions={<button className="btn" onClick={detail.reload}>Обновить</button>}>
          <ErrorBox error={detail.error} onRetry={detail.reload} />
          <div className="kv-grid" style={{ marginBottom: 8 }}>
            <KV k="Статус" v={<StatusBadge value={runStatus || 'pending'} />} />
            <KV k="Движок" v={run?.engine ?? '—'} />
            <KV k="Запущен" v={fmtDate(run?.started_at ?? run?.created_at)} />
            <KV k="Завершён" v={fmtDate(run?.finished_at)} />
            {source && (
              <KV k="Источник" v={source.kind === 'message'
                ? `письмо «${source.subject || ''}»${source.sender ? ` от ${source.sender}` : ''}`
                : (
                  <span>
                    {source.file_name || 'файл'}
                    {source.file_blob_id && (
                      <>
                        {' — '}
                        <a href={documents.downloadInlineUrl(source.file_blob_id)}
                           target="_blank" rel="noreferrer">открыть</a>
                      </>
                    )}
                  </span>
                )} />
            )}
          </div>
          {runLimited && (
            <div className="alert alert-warn">
              <strong>Ограниченный режим.</strong>
              <span>
                Извлечено только правилами — внешний ИИ недоступен
                {runLimitReason ? ` (${LIMIT_REASON_LABEL[runLimitReason] || runLimitReason})` : ''}.
              </span>
            </div>
          )}
          {(runStatus === 'pending' || runStatus === 'running') && (
            <div className="muted">Извлечение выполняется — страница обновится автоматически.</div>
          )}
          {runStatus === 'error' && (
            <div className="alert alert-error">
              <strong>Ошибка извлечения.</strong>
              <span>{RUN_ERROR_TEXT[run?.error_code ?? detail.data?.error_code ?? ''] ?? `Код: ${run?.error_code ?? detail.data?.error_code ?? '—'}`}</span>
            </div>
          )}

          {runStatus === 'done' && fields.length === 0 && !detail.loading && (
            <Empty text="Полей-кандидатов не извлечено" />
          )}

          {fields.length > 0 && (
            <>
              <h3 style={{ margin: '8px 0 4px' }}>Данные заявки</h3>
              {requestFields.length
                ? renderFieldsTable(requestFields, false)
                : <Empty text="Нет кандидатов для данных заявки" />}
              {partyFields.length > 0 && (
                <>
                  <h3 style={{ margin: '16px 0 4px' }}>Карточка компании</h3>
                  <div className="field-hint" style={{ marginBottom: 6 }}>
                    Реквизиты контрагента (заявитель/изготовитель). Применяются
                    к общей карточке компании — отдельное подтверждение,
                    требуется право редактирования карточки.
                  </div>
                  {renderFieldsTable(partyFields, true)}
                </>
              )}
              <ErrorBox error={confirmErr} />
              {conflict && (
                <div className="alert alert-error">
                  <strong>Конфликт версий (409).</strong>
                  <span>Данные изменены параллельно — обновите сравнение и повторите подтверждение.</span>
                  <button className="btn" onClick={() => { setConflict(false); setDecisions({}); detail.reload(); data.reload(); }}>
                    Обновить сравнение
                  </button>
                </div>
              )}
              {confirmNotice && <div className="alert alert-info">{confirmNotice}</div>}
              <div className="form-actions" style={{ marginTop: 8 }}>
                <button className="btn" onClick={acceptAllDiffs}>Принять все расхождения</button>
                <button className="btn primary" disabled={busy || decidedCount === 0}
                        onClick={() => void confirm()}>
                  {busy ? 'Подтверждение…' : `Подтвердить решения (${decidedCount})`}
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {runId && (runDocKind === 'invoice' || runDocKind === 'act')
        && (runStatus === 'done' || runStatus === 'partial') && (
        <FinanceSection runId={runId} docKind={runDocKind}
                        onChanged={() => { runsHistory.reload(); }} />
      )}
    </div>
  );
}
